import {Button, Dropdown, Label, Pagination} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {ChevronUpIcon} from "@project/icons-web";

import {componentMessages} from "../../components.messages";

interface TablePaginationProps {
  /** The currently active page (1-indexed) */
  currentPage: number;
  /** Number of items per page (used to compute total pages and item-range summary) */
  pageSize: number;
  /** Number of items in the current page's data array */
  currentPageItemCount: number;
  /** Grand total number of items across all pages */
  totalItems: number;
  /** Called with the target page number when the user navigates */
  onPageChange: (page: number) => void;
  /** Called with the target page size when the user selects a new density */
  onPageSizeChange?: (pageSize: number) => void;
  /** Visual size of the pagination component (default: "sm") */
  size?: "sm" | "md" | "lg";
  /** Whether the pagination controls are disabled */
  isDisabled?: boolean;
}

/**
 * TablePagination — full-featured pagination bar for data tables.
 *
 * Internalizes three concerns that would otherwise pollute page orchestrators:
 * 1. The `getPaginationItems` algorithm (which page numbers + ellipses to show)
 * 2. The item-range formula (`startItem` / `endItem` computation)
 * 3. The full HeroUI `Pagination.*` compound composition
 *
 * Callers provide only the minimal set of values needed: current page, page
 * size, current page's item count, and total items. Everything else is derived.
 *
 * @example
 * ```tsx
 * <TablePagination
 *   currentPage={visiblePage}
 *   pageSize={data.perPage}
 *   currentPageItemCount={data.data.length}
 *   totalItems={data.total}
 *   onPageChange={handlePageChange}
 * />
 * ```
 */
export function TablePagination({
  currentPage,
  currentPageItemCount,
  onPageChange,
  onPageSizeChange,
  pageSize,
  size = "sm",
  totalItems,
  isDisabled,
}: TablePaginationProps) {
  const {t} = useI18n();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = totalItems === 0 ? 0 : startItem + currentPageItemCount - 1;
  const paginationItems = getPaginationItems(currentPage, totalPages);
  const currentPageSizeLabel = t(componentMessages.tablePaginationPageSize, {pageSize});

  return (
    <Pagination
      className="flex w-full flex-row items-center justify-center @lg:justify-between"
      size={size}
    >
      <Pagination.Summary className="hidden @lg:block">
        <div className="flex items-center gap-2">
          {onPageSizeChange && (
            <Dropdown>
              <Button
                className="text-muted text-xs"
                size={size}
                variant="ghost"
                isDisabled={isDisabled}
              >
                {currentPageSizeLabel}
                <ChevronUpIcon />
              </Button>
              <Dropdown.Popover placement="top">
                <Dropdown.Menu
                  selectedKeys={new Set([pageSize.toString()])}
                  selectionMode="single"
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0];

                    if (selected && onPageSizeChange) {
                      onPageSizeChange(Number(selected));
                    }
                  }}
                >
                  {[10, 20, 30, 50, 70, 100].map((sizeOption) => {
                    const sizeLabel = t(componentMessages.tablePaginationPageSize, {
                      pageSize: sizeOption,
                    });

                    return (
                      <Dropdown.Item
                        id={sizeOption.toString()}
                        key={sizeOption}
                        textValue={sizeLabel}
                      >
                        <Dropdown.ItemIndicator />
                        <Label>{sizeLabel}</Label>
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          )}
          <span className="text-xs">
            {t(componentMessages.tablePaginationSummary, {endItem, startItem, totalItems})}
          </span>
        </div>
      </Pagination.Summary>
      <Pagination.Content>
        <Pagination.Item>
          <Pagination.Previous
            isDisabled={isDisabled || currentPage <= 1}
            onPress={() => onPageChange(currentPage - 1)}
          >
            <Pagination.PreviousIcon />
            <span className="hidden @lg:inline">
              {t(componentMessages.tablePaginationPrevious)}
            </span>
          </Pagination.Previous>
        </Pagination.Item>
        {paginationItems.map((item, index) =>
          item === "ellipsis" ? (
            <Pagination.Item key={`ellipsis-${index}`}>
              <Pagination.Ellipsis />
            </Pagination.Item>
          ) : (
            <Pagination.Item key={item}>
              <Pagination.Link
                isDisabled={isDisabled}
                isActive={item === currentPage}
                onPress={() => onPageChange(item)}
              >
                {item}
              </Pagination.Link>
            </Pagination.Item>
          ),
        )}
        <Pagination.Item>
          <Pagination.Next
            isDisabled={isDisabled || currentPage >= totalPages}
            onPress={() => onPageChange(currentPage + 1)}
          >
            <span className="hidden @lg:inline">{t(componentMessages.tablePaginationNext)}</span>
            <Pagination.NextIcon />
          </Pagination.Next>
        </Pagination.Item>
      </Pagination.Content>
    </Pagination>
  );
}

/**
 * Computes the list of pagination items (page numbers and ellipsis markers).
 * Always shows first/last page, the current page, and its immediate neighbors.
 * Gaps larger than one page are replaced with an "ellipsis" sentinel.
 */
function getPaginationItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({length: totalPages}, (_, i) => i + 1);
  }

  const visiblePages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const sortedPages = Array.from(visiblePages)
    .filter((p) => p > 0 && p <= totalPages)
    .sort((a, b) => a - b);

  return sortedPages.reduce<Array<number | "ellipsis">>((items, page) => {
    const prev = items[items.length - 1];

    if (typeof prev === "number") {
      if (page - prev === 2) items.push(prev + 1);
      else if (page - prev > 2) items.push("ellipsis");
    }

    items.push(page);

    return items;
  }, []);
}
